import type { StepPlanInfoId } from '@az3fr/core';
import { memo, useCallback, useState } from 'react';

import { Slider } from '@/components/ui/slider';
import { SetsEditorV2 } from '@/features/workout-creator/ui/v2/sets-editor';
import { StepPlanDetails } from '@/features/workout-creator/ui/v2/step-editor-v2/components/StepPlanDetails';
import { StepPlanHeader } from '@/features/workout-creator/ui/v2/step-editor-v2/components/StepPlanHeader';
import { useModeSwitcherLogic } from '@/features/workout-creator/ui/v2/step-editor-v2/hooks/useModeSwitcherLogic';
import { useStepEditorLogic } from '@/features/workout-creator/ui/v2/step-editor-v2/hooks/useStepEditorLogic';

interface StepEditorV2Props {
	orderIndex: number;
	stepPlanId: StepPlanInfoId;
	onStepDelete: (stepPlanId: StepPlanInfoId) => void;
}

function StepEditorV2({ orderIndex, stepPlanId, onStepDelete }: StepEditorV2Props) {
	const [isCollapsed, setIsCollapsed] = useState(false);

	// Extract all business logic into custom hooks
	const { stepPlan, sectionPlan, exercise, handleNoteChange, switchRepetitionMode } =
		useStepEditorLogic(stepPlanId);

	const { shouldShowModeSwitcher, isDisabled } = useModeSwitcherLogic({
		sectionType: sectionPlan?.sectionType ?? 'standard',
		sectionRounds: sectionPlan?.rounds ?? null,
	});

	const handleDeleteStep = useCallback(() => {
		onStepDelete(stepPlanId);
	}, [onStepDelete, stepPlanId]);

	const handleExpandToggle = useCallback(() => {
		setIsCollapsed(!isCollapsed);
	}, [isCollapsed]);

	if (!stepPlan) {
		return null;
	}

	return (
		<div
			key={stepPlanId}
			className="overflow-hidden border-gray-200 border-x border-b bg-white last:rounded-b-xl hover:bg-gray-50/50"
		>
			{/* --- Step Plan Header --- */}
			<StepPlanHeader
				movementName={exercise?.movement?.name ?? ''}
				primaryMuscleName={exercise?.primaryMuscleGroupName ?? ''}
				stepOrderIndex={orderIndex}
				onDeleteStepPlan={handleDeleteStep}
				equipmentName={exercise?.equipment.name ?? ''}
				isCollapsed={isCollapsed}
				onExpandStepPlan={handleExpandToggle}
			/>

			{/* --- Step Plan Details Collapsible (animated) --- */}
			<Slider isOpen={!isCollapsed} transitionDuration={200}>
				<div className="bg-white px-3.5 pt-3 pb-3.5 sm:p-4">
					<StepPlanDetails
						stepPlanId={stepPlanId}
						notes={stepPlan.notes}
						onNoteChange={handleNoteChange}
						repetitionMode={stepPlan.repetitionMode}
						onRepetitionModeChange={switchRepetitionMode}
						sectionType={sectionPlan?.sectionType ?? 'standard'}
						sectionRounds={sectionPlan?.rounds ?? null}
						shouldShowModeSwitcher={shouldShowModeSwitcher}
						isModeSwitcherDisabled={isDisabled}
					/>
					<SetsEditorV2 stepPlanId={stepPlanId} />
				</div>
			</Slider>
		</div>
	);
}

// Memoize the component to prevent unnecessary rerenders
const MemoizedStepEditorV2 = memo(StepEditorV2);

export { MemoizedStepEditorV2 as StepEditorV2 };
